<script setup lang="ts">
/**
 * Blender 风格节点外壳 —— 纯展示组件，**零图引擎依赖**。
 *
 * 设计意图：把「节点长什么样」和「图怎么连」彻底解耦。
 *   · 只接收一个 GraphNodeModel，渲染 header / 行 / 内联控件
 *   · 连线由上层处理；端口位置通过插槽交给适配层注入 <Handle>
 *   · 因此可以脱离任何图引擎单独调试样式
 *
 * 行模型有五种形态（输出 / 属性 / 输入 / 分组 / 展示），
 * 硬约束：**输入与输出不在同一行**。详见 types/node.ts。
 *
 * 分组归集与折叠锚点的计算全在 node/rows.ts 里（纯函数，可单测），
 * 本组件只负责把 buildRenderList() 的结果画出来。
 */
import { computed, ref, nextTick } from 'vue'
import { socketVisualState } from '../node/socketState'
import type { SocketVisualState } from '../node/socketState'
import type {
  DisplayRow,
  GraphNodeModel,
  GroupRow,
  NodeDiagnostic,
  NodeRow,
  SocketDef,
} from '../types/node'
import type { Widget, WidgetValue } from '../types/widget'
import type { NodeActionDef } from '../node/package'
import { categoryColorVar, MIN_NODE_WIDTH } from '../theme/tokens'
import {
  MIN_FRAME_HEIGHT,
  MIN_FRAME_WIDTH,
} from '../types/node'
import {
  buildRenderList,
  DEFAULT_DISPLAY_ROW_HEIGHT,
  hiddenPortCount,
  projectNodeSockets,
} from '../node/rows'
import type { ProjectedSocket, RenderItem } from '../node/rows'
import { useRepeaterDrag } from './composables/useRepeaterDrag'
import { useNodeRename } from './composables/useNodeRename'
import { useRowNaming } from './composables/useRowNaming'
import NpSocket from './NpSocket.vue'
import NpNodeBox from './rows/NpNodeBox.vue'
import NpNodeHeader from './rows/NpNodeHeader.vue'
import NpRowDisplay from './rows/NpRowDisplay.vue'
import NpRowGroup from './rows/NpRowGroup.vue'
import NpRowInput from './rows/NpRowInput.vue'
import NpRowOutput from './rows/NpRowOutput.vue'
import NpRowProperty from './rows/NpRowProperty.vue'
import NpRowRepeater from './rows/NpRowRepeater.vue'

const props = withDefaults(
  defineProps<{
    model: GraphNodeModel
    selected?: boolean
    /**
     * 活动节点 —— 多选时属性面板跟随的那一个。
     * 和 selected 是两件事：选中可以有很多个，活动只有一个。
     */
    active?: boolean
    collapsed?: boolean
    /** 是否正在拖拽连线（用于端口高亮/变暗） */
    linking?: boolean
    /** 拖线时，可作为合法落点的 socket id 列表；null = 不做区分 */
    connectableSockets?: string[] | null
    /** 是否允许拖右边缘改宽 */
    resizable?: boolean
  /**
   * 有内部子图（组 / 循环）—— 底部显示一行「双击进入」。
   *
   * 之前进入子图这件事**完全没有提示**：用户要么猜，要么看文档。
   */
  hasSubgraph?: boolean
  /**
   * 这个节点类型**是干什么的**（一句话）。
   *
   * 由宿主给 —— 库只知道 `title` / `type` / `category`，
   * 「作用」是领域知识，属于集成方。
   */
  typeHint?: string
  /**
   * **类型图标**（24×24 stroke 路径）—— 由宿主按类型现查（`NodeTypeDef.icon`）。
   *
   * 它解决的是"标题被改过之后认不出这是什么节点"：图标是**类型**的信号，
   * 名字是**用户**的信号。没给就回落成 header 上那个「!」。
   */
  typeIcon?: string
  /**
   * **节点头部上的动作按钮**（按类型声明，见 `NodeActionDef`）。
   *
   * ⚠️ 库只负责画和往上报 —— "这个 id 干什么"是宿主的事
   * （打开属性面板要切窗口，库不认识窗口）。
   */
  actions?: readonly NodeActionDef[]
    /**
     * 显示「＋ 加一行」。
     *
     * 由宿主决定 —— 只有组 / 循环内部那两个接口节点需要它：
     * 它们的行就是**接口定义**，得能一行一行加出来。
     * 普通节点的行是类型定义的一部分，不该让用户随手加。
     */
    /** 最小宽度 px —— 所有节点共用，保证视觉基线一致 */
    minWidth?: number
    /** 该节点的诊断（求值失败、类型未注册…）。有则在 header 显示徽标 */
    diagnostics?: NodeDiagnostic[]
    /** 本次拖拽起点若在本节点上，这里是它的 handle id */
    originSocket?: string | null
  }>(),
  {
    selected: false,
    active: false,
    collapsed: false,
    linking: false,
    connectableSockets: null,
    resizable: false,
    minWidth: MIN_NODE_WIDTH,
    diagnostics: () => [],
    originSocket: null,
  },
)

const emit = defineEmits<{
  (e: 'update:collapsed', value: boolean): void
  (e: 'group-toggle', groupId: string, collapsed: boolean): void
  (e: 'widget-update', rowId: string, widgetId: string, value: WidgetValue): void
  (e: 'resize', width: number): void
  (e: 'repeater-remove', rowId: string, index: number): void
  (e: 'toggle-mute', muted: boolean): void
  /** 只切换**这个节点**的端口折叠（Ctrl+H 是所有选中节点） */
  (e: 'toggle-hide-unused'): void
  /** 变参行拖拽排序（只对已连线的实例触发） */
  (e: 'repeater-reorder', rowId: string, from: number, to: number): void
  /** 多行文本域被拖高 —— 这也是「把节点改高」的方式 */
  (e: 'widget-resize', rowId: string, widgetId: string, height: number): void
  /** 框 / 注释被拖右下角改尺寸（宽**和**高） */
  (e: 'box-resize', width: number, height: number): void
  /** 注释正文变化 */
  (e: 'note-update', text: string): void
  /**
   * 行上的小开关被点了（目前只有循环输出行的「跳过空值」）。
   *
   * 用 rowId + 新值，而不是直接改 model —— 组件不持有状态。
   */
  (e: 'row-toggle', rowId: string, value: boolean): void
  /** 双击标题改名。空字符串 = 恢复默认标题 */
  (e: 'rename', label: string): void
  /** 双击变参行实例名改名。空字符串 = 恢复自动/占位名 */
  (e: 'row-rename', rowId: string, index: number, name: string): void
  /** 变参行加一个实例（先摆一行空着等接线） */
  (e: 'repeater-add', rowId: string): void
  /** 接口节点加一行（组 / 循环的输入输出节点用） */
  /** 点「进入内部」—— 组 / 循环才有 */
  (e: 'enter-subgraph'): void
  /** 头部动作按钮（id 由节点类型声明） */
  (e: 'node-action', id: string): void
}>()

/**
 * 显式声明插槽契约。
 *
 * 必须显式声明 —— 同一个插槽在多处渲染，靠模板绑定推断出来的类型会漏字段。
 */
defineSlots<{
  /** 真实端口：适配层在这里注入图引擎的**可拖拽** Handle */
  'socket-input'(props: {
    def: SocketDef
    state: SocketVisualState
    row: NodeRow
  }): any
  'socket-output'(props: {
    def: SocketDef
    state: SocketVisualState
    row: NodeRow
  }): any
  /**
   * **锚点**：只用来挂住已有的线。
   * 不渲染圆点、不可拖拽（防止从折叠区拉出新线），全部落在同一位置。
   *
   * 两处会用到：折叠的分组行、以及整体折叠的节点（此时 group 为 undefined）。
   */
  'anchor-input'(props: { sockets: SocketDef[]; group?: GroupRow }): any
  'anchor-output'(props: { sockets: SocketDef[]; group?: GroupRow }): any
  /** 自定义控件（kind: 'custom' 的 widget） */
  'widget-custom'(props: { value: unknown; widget: Widget; row: NodeRow }): any
  /** 展示行的内容 */
  'row-display'(props: { row: DisplayRow }): any
  /** header 右侧附加内容 */
  'header-extra'(): any
}>()

/* ── 布局 ───────────────────────────────────────────────────── */

/**
 * 圆点区域的尺寸。
 *
 * 显式给死而不是靠 CSS —— 这是**正方形**，宽高必须一致，
 * 用 CSS 的 `width: 100%` 会跟着父容器走。
 *
 * ⚠️ 这里的高度只表示**圆点区**，不含头部。
 * 节点总高 = 这个高度 + 头部的 `--np-dot-header-h`
 * （`.np-node--dot` 用 `box-sizing: content-box` + `padding-top` 把它加上去）。
 * 所以**不要**在这里自己加头部高度 —— 那样端口居中的计算会跟着错。
 */
const dotStyle = computed(() => {
  const side = props.model.width ?? 18
  return { width: `${side}px`, height: `${side}px` }
})

/** 框形态：一个可以拖动、可以改尺寸的矩形，没有端口 */
const frameMode = computed(() => props.model.render === 'frame')

const noteMode = computed(() => props.model.render === 'note')

/** 空数组常量 —— 模板里每次生成新数组会让子组件无谓重渲染 */
const EMPTY_DIAGNOSTICS: never[] = []

/* ── 就地改名 ─────────────────────────────────────────────── */


/* ── 变参行实例名 ─────────────────────────────────────────── */


/**
 * 某个变参实例显示什么标签。
 *
 * 接了线 → 用上游那个端口的名字；没接 → 回退到行自己的标签
 * （`监听 2` 这种）。
 */
/**
 * 渲染项在变参行里的序号。
 *
 * 普通行没有 index（渲染项的联合类型里是可选的），
 * 模板里不能对它做非空断言 —— 所以统一从这里取。
 */
function rowIndex(item: unknown): number {
  const value = (item as { index?: unknown } | null)?.index
  return typeof value === 'number' ? value : 0
}

/**
 * 是不是这一变参行的最后一个实例。
 *
 * 渲染项是联合类型（普通行没有 index/total），所以不能直接摸字段 ——
 * 这个坑已经踩过一次（TS 会报「Property 'index' does not exist」）。
 */
function isLastInstance(item: unknown): boolean {
  const value = item as { index?: number; total?: number } | null
  return (
    typeof value?.index === 'number' &&
    typeof value?.total === 'number' &&
    value.index === value.total - 1
  )
}

function instanceLabel(item: { row: NodeRow; index?: number }): string | undefined {
  // 普通行没有 index，也不需要自动标签
  if (item.row.kind !== 'repeater' || item.index === undefined) return undefined

  // 用户起的名优先，其次才是自动跟随上游
  const named = item.row.names?.[item.index] ?? item.row.labels?.[item.index]
  if (named) return named

  // 回退：行标签 + 序号。不带序号的话每个实例看起来一模一样，分不清第几路
  return item.row.label ? `${item.row.label} ${item.index + 1}` : undefined
}

/*
 * ★ 两组「就地改名」**已向下拆到** `composables/` 下的两个文件：
 *   · `useNodeRename.ts` —— 节点标题（emit `rename`）
 *   · `useRowNaming.ts`  —— 变参行实例名（emit `row-rename`）
 *
 * ⚠️ 刻意拆成**两个**文件：量下来两组声明的名字**交集为 0**、
 * 各自 emit 不同的事件 —— 合成一个「改名模块」等于把它们重新绑在一起。
 */
const { renaming, renameEl, typeTooltip, startRename, prefixed, cancelRename } = useNodeRename({
  model: () => props.model,
  typeHint: () => props.typeHint,
  rename: (label) => emit('rename', label),
})

const { renamingRow, setRowRenameEl, startRowRename, cancelRowRename, commitRowRename } =
  useRowNaming({
    renameRow: (rowId, index, value) => emit('row-rename', rowId, index, value),
    rowIndex,
  })

/** 圆点形态：不画 header / 行，只留左右两个端口 */
const dotMode = computed(() => props.model.render === 'dot')
const dotInput = computed(() =>
  props.model.rows.find((row) => row.kind === 'input'),
)
const dotOutput = computed(() =>
  props.model.rows.find((row) => row.kind === 'output'),
)

/** 折叠了几个端口（没开折叠就是 0，标记不显示） */
const hiddenPorts = computed(() =>
  props.model.hideUnused ? hiddenPortCount(props.model.rows) : 0,
)

const renderList = computed(() =>
  buildRenderList(props.model.rows, { hideUnused: props.model.hideUnused }),
)

const rootStyle = computed<Record<string, string>>(() => {
  const style: Record<string, string> = {
    /*
     * ⚠️ 这里塞的是**变量引用**，不是色值。
     *
     * 改动前是 `nodeCategoryColor(category)` 算出来的十六进制 ——
     * 值在模块加载时就固化了，主题（以及任何 CSS）都改不动节点头颜色。
     * 现在 JS 只负责拼「去查哪个变量」，色值住在 `theme.css` 的
     * `--np-cat-*` 令牌里。
     */
    '--np-header-color': categoryColorVar(props.model.category),
    '--np-node-min-width': `${props.minWidth}px`,
  }
  if (props.model.width) style.width = `${props.model.width}px`
  return style
})

/* ── 端口状态 ───────────────────────────────────────────────── */

function inputState(def: SocketDef | undefined): SocketVisualState {
  return socketVisualState(
    def,
    props.linking,
    props.connectableSockets,
    props.originSocket,
  )
}

function outputState(def: SocketDef | undefined): SocketVisualState {
  return socketVisualState(
    def,
    props.linking,
    props.connectableSockets,
    props.originSocket,
  )
}

/** header 徽标的严重级别：取最严重的一个 */
const worstSeverity = computed(() => {
  const list = props.diagnostics ?? []
  if (list.some((d) => d.severity === 'error')) return 'error'
  if (list.some((d) => d.severity === 'warning')) return 'warning'
  return list.length > 0 ? 'info' : null
})

const diagnosticsTitle = computed(() =>
  (props.diagnostics ?? []).map((d) => d.message).join('\n'),
)

/* ── 折叠分组的锚点 ─────────────────────────────────────────── */

function groupAnchors(item: RenderItem, side: 'input' | 'output'): SocketDef[] {
  if (item.kind !== 'group') return []
  return item.anchors
    .filter((a: ProjectedSocket) => a.side === side)
    .map((a) => a.socket)
}

/** 节点整体折叠时的锚点（遍历全部行，不区分分组） */
const collapsedAnchors = computed(() => projectNodeSockets(props.model.rows))

function nodeAnchors(side: 'input' | 'output'): SocketDef[] {
  return collapsedAnchors.value
    .filter((a: ProjectedSocket) => a.side === side)
    .map((a) => a.socket)
}

/* ── 事件转发 ───────────────────────────────────────────────── */

function onWidgetUpdate(row: NodeRow, widgetId: string, value: WidgetValue) {
  emit('widget-update', row.id, widgetId, value)
}

/**
 * 整行可点击折叠，不只是那个小三角。
 *
 * ⚠️ 翻的是**解析后的状态**（`resolvedCollapsed`），不是 `group.collapsed`：
 * 后者的初始值是 `undefined`（默认收起由 `row.expanded` 决定），
 * 直接取反会算出"收起一个本来就收起的段"—— 用户点一下没反应。
 */
function onGroupToggle(group: GroupRow) {
  emit('group-toggle', group.id, !resolvedCollapsed(group))
}

/** 这个段现在收起了吗（用户翻过就听用户的，否则看声明里的默认值） */
function resolvedCollapsed(group: GroupRow): boolean {
  return group.collapsed ?? !group.expanded
}

/* ── 变参行拖拽排序 ─────────────────────────────────────────── */

/*
 * ★ 变参行拖拽排序（约 100 行）**已向下拆到** `composables/useRepeaterDrag.ts`：
 * 它是那五组指针交互里**外部依赖最少**的一组（只 emit 一个事件）。
 *
 * ⚠️ 那里记了这块最要紧的一条规矩：**`pointerdown` 时不能立刻捕获指针**
 * （会让双击失效）—— 改这块之前先读那段注释。
 */
const { repeaterDrag, onRepeaterDragStart, onRepeaterDragMove, onRepeaterDragEnd } =
  useRepeaterDrag({
    reorder: (rowId, from, to) => emit('repeater-reorder', rowId, from, to),
  })

/* ── 拉宽 ───────────────────────────────────────────────────── */

const resizing = ref(false)
let resizeStartX = 0
let resizeStartWidth = 0
/** 屏幕像素 / 布局像素 —— 画布缩放或任意 CSS transform 的比值 */
let resizeScale = 1

/**
 * 注释正文的输入。
 *
 * 不走 `widget-update` —— 那条通道是「行上的控件」，注释没有行。
 */
function onNoteInput(ev: Event) {
  emit('note-update', (ev.target as HTMLTextAreaElement).value)
}

/* ── 框 / 注释：右下角拖拽改尺寸（宽和高一起） ───────────────── */

let boxResizeStart = { x: 0, y: 0, width: 0, height: 0 }
let boxResizeScale = 1

function onBoxResizeStart(ev: PointerEvent) {
  if (ev.button !== 0) return
  ev.preventDefault()

  const nodeEl = (ev.currentTarget as HTMLElement).parentElement
  if (!nodeEl) return

  // 跟改宽一样：用「实测 / 布局」反解画布缩放，本组件不需要知道图引擎状态
  const rect = nodeEl.getBoundingClientRect()
  boxResizeScale = nodeEl.offsetWidth > 0 ? rect.width / nodeEl.offsetWidth : 1

  boxResizeStart = {
    x: ev.clientX,
    y: ev.clientY,
    // 基准用布局尺寸，rect 是屏幕像素 —— 混用会让尺寸跳变
    width: nodeEl.offsetWidth,
    height: nodeEl.offsetHeight,
  }
  resizing.value = true
  ;(ev.currentTarget as HTMLElement).setPointerCapture(ev.pointerId)
}

function onBoxResizeMove(ev: PointerEvent) {
  if (!resizing.value) return
  const dx = (ev.clientX - boxResizeStart.x) / boxResizeScale
  const dy = (ev.clientY - boxResizeStart.y) / boxResizeScale
  emit(
    'box-resize',
    Math.max(MIN_FRAME_WIDTH, Math.round(boxResizeStart.width + dx)),
    Math.max(MIN_FRAME_HEIGHT, Math.round(boxResizeStart.height + dy)),
  )
}

function onBoxResizeEnd(ev: PointerEvent) {
  if (!resizing.value) return
  resizing.value = false
  ;(ev.currentTarget as HTMLElement).releasePointerCapture?.(ev.pointerId)
}

function onResizeStart(ev: PointerEvent) {
  if (ev.button !== 0) return
  ev.preventDefault()

  const nodeEl = (ev.currentTarget as HTMLElement).parentElement
  if (!nodeEl) return

  // 画布 zoom 会让「屏幕像素」与「布局像素」不一致。
  // 用 实测宽度 / 布局宽度 反解出当前缩放，这样本组件不需要知道
  // 图引擎的缩放状态，也顺便兼容任何外部 CSS transform。
  const rect = nodeEl.getBoundingClientRect()
  resizeScale = nodeEl.offsetWidth > 0 ? rect.width / nodeEl.offsetWidth : 1

  // 基准必须用布局宽度（CSS px）；rect.width 是屏幕像素，混用会让宽度跳变
  resizeStartWidth = nodeEl.offsetWidth
  resizeStartX = ev.clientX
  resizing.value = true
  ;(ev.currentTarget as HTMLElement).setPointerCapture(ev.pointerId)
}

function onResizeMove(ev: PointerEvent) {
  if (!resizing.value) return
  // 屏幕位移换算回布局像素 —— 否则缩放后拖拽长度会失真
  const delta = (ev.clientX - resizeStartX) / (resizeScale || 1)
  emit('resize', Math.round(Math.max(props.minWidth, resizeStartWidth + delta)))
}

function onResizeEnd(ev: PointerEvent) {
  if (!resizing.value) return
  resizing.value = false
  ;(ev.currentTarget as HTMLElement).releasePointerCapture(ev.pointerId)
}
</script>

<template>
  <!--
    圆点形态（接通 / 重路由节点）。
    不画 header、不画行 —— 只有左右两个端口。
    端口位置不用自己算：Vue Flow 会把 Position.Left/Right 的 Handle
    定位到节点的左右**垂直居中**处，所以这里只要把插槽渲染出来就行。
  -->
  <div
    v-if="dotMode"
    class="np-node np-node--dot"
    :class="{ 'is-selected': selected, 'is-active': active, 'is-muted': !!model.muted }"
    :style="dotStyle"
    :data-node-id="model.id"
    :data-node-type="model.type"
  >
    <!--
      头部（拖拽把手）。

      ⚠️ 为什么圆点节点也需要一个头：
      18×18 的方块在手机上根本抓不住；更糟的是端口在触摸端的命中区
      被 `::after { inset: -9px }` 放大到约 34px，左右两侧的命中区
      **盖住了中间那点可拖的区域** —— 于是「拖不动」（和
      `graph/canvas/np-canvas.css` 里记录的那次「拉宽手柄抢走端口」
      是同一类问题，只是方向相反）。

      头部给了三件事：
        · 一块明确属于「拖动」而不是「连线」的区域；
        · `cursor: move` 的光标提示；
        · 触摸端把这块长到 20px，加上圆点区就有 38px+ 可抓。
    -->
    <div class="np-node__dot-header" :title="model.label || model.title || '拖动移动'">
      <span class="np-node__dot-grip" aria-hidden="true">
        <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor">
          <circle cx="2" cy="2" r="1" /><circle cx="6" cy="2" r="1" />
          <circle cx="2" cy="6" r="1" /><circle cx="6" cy="6" r="1" />
        </svg>
      </span>
    </div>
    <slot
      v-if="dotInput"
      name="socket-input"
      :def="dotInput.socket"
      :state="inputState(dotInput.socket)"
      :row="dotInput"
    />
    <slot
      v-if="dotOutput"
      name="socket-output"
      :def="dotOutput.socket"
      :state="outputState(dotOutput.socket)"
      :row="dotOutput"
    />
    <span class="np-node__dot" aria-hidden="true" />
  </div>

  <!--
    注释形态：标题栏 + 一整块可编辑的正文。
    和框共用尺寸逻辑（显式宽高 + 右下角缩放），区别只是里面能写字。
  -->
  <!--
    注释形态 / 框形态：共用「显式宽高 + 右下角缩放」的外壳。
    细节见 NpNodeBox.vue 的文件头注释。
  -->
  <NpNodeBox
    v-else-if="noteMode || frameMode"
    :model="model"
    :selected="selected"
    :active="active"
    :note="noteMode"
    @box-resize="(w, h) => emit('box-resize', w, h)"
    @note-update="(text) => emit('note-update', text)"
  />

  <div
    v-else
    class="np-node"
    :class="{
      'is-selected': selected,
      'is-active': active,
      'is-collapsed': collapsed,
      'is-muted': !!model.muted,
      'is-resizing': resizing,
    }"
    :style="rootStyle"
    :data-node-id="model.id"
    :data-node-type="model.type"
    :data-category="model.category"
    :data-render="model.render ?? 'node'"
  >
    <NpNodeHeader
      :model="model"
      :collapsed="collapsed"
      :hidden-ports="hiddenPorts"
      :diagnostics="props.diagnostics ?? EMPTY_DIAGNOSTICS"
      :type-hint="props.typeHint"
      :type-icon="props.typeIcon"
      :actions="props.actions"
      @action="(id) => emit('node-action', id)"
      @update:collapsed="(v) => emit('update:collapsed', v)"
      @rename="(label) => emit('rename', label)"
      @toggle-mute="(m) => emit('toggle-mute', m)"
      @toggle-hide-unused="emit('toggle-hide-unused')"
    >
      <template #header-extra>
        <slot name="header-extra" />
      </template>
    </NpNodeHeader>

    <!--
      节点整体折叠：body 不渲染（所有行级 Handle 都没了），
      所以把已连接的端口锚定到节点左右边缘，让线还能挂住。
      位置由 Handle 的默认 top:50% 决定 —— 折叠节点只有 header 高，
      所以锚点就落在它的垂直中心，不会上下错开。
    -->
    <template v-if="collapsed">
      <slot name="anchor-input" :sockets="nodeAnchors('input')" />
      <slot name="anchor-output" :sockets="nodeAnchors('output')" />
    </template>

    <!--
      组 / 循环：**进入内部**的入口。
      ⚠️ 从「双击节点」改成这里 —— 双击和「标题改名」「变参行排序」
      都会撞车，而且没有任何提示。一个明确的按钮更靠谱。
    -->
    <button
      v-if="props.hasSubgraph && !collapsed"
      class="np-node__hint nodrag"
      type="button"
      title="进入这个组 / 循环的内部编辑"
      @pointerdown.stop
      @click.stop="emit('enter-subgraph')"
    >
      <span class="np-node__hint-icon">⤵</span>
      进入内部
      <span class="np-node__hint-count">{{ model.graph?.nodes.length ?? 0 }} 个节点</span>
    </button>

    <!--
      接口节点的「＋ 加一行」。
      组 / 循环内部的行就是接口定义本身 —— 没有这个按钮就加不出新端口，
      也就没有「等着接线的那一行」。
    -->
    <!--
      ⚠️ 这里原来有一个「＋ 加一行」按钮（要 `addRow` prop 才渲染）。
      **已去掉** —— 接口节点的参数靠**接线自己长**（和输出结果那条行同一条规矩：
      件数从边推，见 `groupInterfaceOf`）。用户的原话：
      「组的输入项不需要按钮等外物来增加参数」。
      没有外部世界需要这个按钮，所以连 prop 一起删，而不是留一个恒不渲染的分支。
    -->

    <div v-if="!collapsed" class="np-node__body">
      <div v-if="!renderList.length" class="np-node__empty">无内容</div>

      <template v-for="item in renderList" :key="item.key">
        <!--
          ⚠️ 六种行形态各自是一个**独立组件**，不再挤在这一个文件里。

          原因很实在：以前它们是一条 v-if 链、靠缩进认分支，
          我在「输出点行」和「变参行」之间连错过三次（改名加错分支、
          nodrag 补错地方、text-align 抄了另一边）。
          `verify:branches` 现在盯着这些约定，但**拆开本身才是根治** ——
          每个分支不到 60 行，一眼能看完。

          每个行组件的 `socket-*` 插槽都必须原样往下传：
          宿主靠它注入**真实可拖拽的端口**；不转发的话画面上端口还在，
          但线连不上。
        -->

        <!-- ══ 分组行 ══ -->
        <NpRowGroup
          v-if="item.kind === 'group'"
          :group="item.group"
          :collapsed="item.collapsed"
          :member-count="item.memberCount"
          :anchors-in="groupAnchors(item, 'input')"
          :anchors-out="groupAnchors(item, 'output')"
          @toggle="onGroupToggle(item.group)"
        >
          <template #anchor-input="slotProps">
            <slot name="anchor-input" v-bind="slotProps" />
          </template>
          <template #anchor-output="slotProps">
            <slot name="anchor-output" v-bind="slotProps" />
          </template>
        </NpRowGroup>

        <!-- ══ 变参行的一个实例 ══ -->
        <NpRowRepeater
          v-else-if="item.kind === 'repeater-row'"
          :row="item.row"
          :index="item.index"
          :total="item.total"
          :in-group="item.inGroup"
          :state="
            (item.row.side ?? 'input') === 'output'
              ? outputState(item.socket)
              : inputState(item.socket)
          "
          :def="item.socket"
          :label="
            instanceLabel(item) || item.row.label || ((item.row.side ?? 'input') === 'output' ? '输出' : '输入')
          "
          :editing="
            renamingRow !== null &&
            renamingRow.rowId === item.row.id &&
            renamingRow.index === item.index
          "
          :dragging="
            repeaterDrag?.rowId === item.row.id && repeaterDrag?.from === item.index
          "
          :drop-target="
            repeaterDrag?.rowId === item.row.id &&
            repeaterDrag?.to === item.index &&
            repeaterDrag?.from !== item.index
          "
          @rename-start="startRowRename(item)"
          @rename-input="(value) => emit('row-rename', item.row.id, item.index, value)"
          @rename-cancel="renamingRow = null"
          @rename-ready="setRowRenameEl"
          @remove="emit('repeater-remove', item.row.id, item.index)"
          @add="emit('repeater-add', item.row.id)"
          @drag-start="onRepeaterDragStart(item, $event)"
          @drag-move="onRepeaterDragMove"
          @drag-end="onRepeaterDragEnd"
        >
          <!--
            ⚠️ **两个端口插槽都要转发**：变参行有输入/输出两个方向。
            只转发一个的话，另一个方向的端口在画面上还在（走默认回退），
            但**线连不上** —— 而且没有任何静态检查会报（`verify:node-branches` 守它）。
          -->
          <template #socket-input="slotProps">
            <slot name="socket-input" v-bind="slotProps" />
          </template>
          <template #socket-output="slotProps">
            <slot name="socket-output" v-bind="slotProps" />
          </template>
        </NpRowRepeater>

        <!-- ══ 普通行 ══ -->
        <template v-else>
          <NpRowOutput
            v-if="item.row.kind === 'output'"
            :row="item.row"
            :in-group="item.inGroup"
            :state="outputState(item.row.socket)"
            :def="item.row.socket"
            @toggle-skip="(value) => emit('row-toggle', item.row.id, value)"
          >
            <template #socket-output="slotProps">
              <slot name="socket-output" v-bind="slotProps" />
            </template>
          </NpRowOutput>

          <NpRowProperty
            v-else-if="item.row.kind === 'property'"
            :row="item.row"
            :in-group="item.inGroup"
            @widget-update="
              (widgetId, value) => onWidgetUpdate(item.row, widgetId, value)
            "
            @widget-resize="
              (widgetId, height) =>
                emit('widget-resize', item.row.id, widgetId, height)
            "
          >
            <template #widget-custom="slotProps">
              <slot name="widget-custom" v-bind="slotProps" :row="item.row" />
            </template>
          </NpRowProperty>

          <NpRowInput
            v-else-if="item.row.kind === 'input'"
            :row="item.row"
            :in-group="item.inGroup"
            :state="inputState(item.row.socket)"
            :def="item.row.socket"
            @widget-update="
              (widgetId, value) => onWidgetUpdate(item.row, widgetId, value)
            "
            @widget-resize="
              (widgetId, height) =>
                emit('widget-resize', item.row.id, widgetId, height)
            "
          >
            <template #socket-input="slotProps">
              <slot name="socket-input" v-bind="slotProps" />
            </template>
            <template #widget-custom="slotProps">
              <slot name="widget-custom" v-bind="slotProps" :row="item.row" />
            </template>
          </NpRowInput>

          <NpRowDisplay
            v-else-if="item.row.kind === 'display'"
            :row="item.row"
            :in-group="item.inGroup"
          >
            <template #row-display="slotProps">
              <slot name="row-display" v-bind="slotProps" />
            </template>
          </NpRowDisplay>
        </template>
      </template>

    </div>

    <!-- 拉宽手柄：只改宽，不改高 -->
    <span
      v-if="resizable && !collapsed"
      class="np-node__resize nodrag"
      role="separator"
      aria-orientation="vertical"
      title="拖动改宽"
      @pointerdown.stop="onResizeStart"
      @pointermove="onResizeMove"
      @pointerup="onResizeEnd"
      @pointercancel="onResizeEnd"
    />
  </div>
</template>
